import { requiereOtpCierre } from "./reglasCierreOtp";
// src/Components/modules/Coordinacion/CerrarTrasladoDialog.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Radio, Typography, Space, Select, Form, Alert, Spin } from "antd";
import { SafetyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useFormPersistence } from "../../../hooks/useFormPersistence";
import { respuestasCierre } from "./respuestasCierre";
import OTPVerificationDialog from "./OTPVerificationDialog";
import api from "../../../axiosConfig";

const { Text } = Typography;

/**
 * Componente CerrarTrasladoDialog para cerrar un traslado seleccionando su estado.
 * Para traslados TAI con estado "Finalizado", requiere verificación OTP.
 */
function CerrarTrasladoDialog({
  open,
  onClose,
  onSubmit,
  allowedStates,
  isTAI = false,
  trasladoId = null,
  estadoActualTraslado = null, // Para detectar si es Xvalidar
}) {
  const [form] = Form.useForm();
  const allStates = ["Finalizado", "Cancelado", "Fallido"];

  // Estado para el diálogo OTP
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  // Estados para verificar si SMS/OTP está activo
  const [smsActivo, setSmsActivo] = useState(true);
  const [otpNormalesActivo, setOtpNormalesActivo] = useState(false);
  const [loadingSmsConfig, setLoadingSmsConfig] = useState(false);
  const [configCargada, setConfigCargada] = useState(false);
  // Control de servicio con la firma retirada por novedad de firma ilegible: el cierre exige OTP siempre
  const [firmaPendienteOtp, setFirmaPendienteOtp] = useState(false);

  const filteredStates = useMemo(() =>
    allStates.filter((s) => allowedStates.includes(s)),
    [allowedStates, allStates]
  );

  // Hook para persistir el estado del formulario
  const formPersistence = useFormPersistence(form);

  // Observar el valor actual del estado en el formulario
  const estadoActual = Form.useWatch('estado', form);

  // Usar el estado del formulario o un valor por defecto
  const selectedEstado = estadoActual || filteredStates[0] || "Finalizado";

  // Cargar configuración de SMS/OTP cuando se abre el diálogo
  const cargarConfigSms = useCallback(async () => {
    try {
      setLoadingSmsConfig(true);
      setConfigCargada(false);
      setFirmaPendienteOtp(false);
      if (trasladoId && ["FinalizadoXvalidar", "FallidoXvalidar"].includes(estadoActualTraslado)) {
        // Se consulta al abrir: el listado puede no traer aún la novedad (llega por WebSocket solo el estado)
        try {
          const control = await api.get(`/control-servicios/traslado/${trasladoId}`);
          setFirmaPendienteOtp(!!control.data?.data?.firma_pendiente_otp);
        } catch (error) {
          if (error.response?.status !== 404) throw error;
        }
      }
      const response = await api.get("/configuraciones/otp");
      if (response.data?.success) {
        setConfigCargada(true);
        setSmsActivo(response.data.data.tai_validacion_sms_activo);
        setOtpNormalesActivo(response.data.data.otp_traslados_normales_activo);
      }
    } catch (error) {
      console.error("Error al cargar config OTP:", error);
      // Por defecto asumir que TAI está activo y normales inactivo si hay error
      setSmsActivo(true);
      setOtpNormalesActivo(false);
    } finally {
      setLoadingSmsConfig(false);
    }
  }, [trasladoId, estadoActualTraslado]);

  // Cargar config cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      cargarConfigSms();
    }
  }, [open, cargarConfigSms]);

  // Detectar si es un estado Xvalidar (pendiente de validación)
  const isPendienteValidacion = ["FinalizadoXvalidar", "FallidoXvalidar"].includes(estadoActualTraslado);

  // Determinar si requiere OTP:
  // - TAI + Finalizado + SMS Activo, o
  // - Normal + Finalizado + OTP Normales Activo
  // Finalizado y Fallido respetan la configuración del tipo de servicio, incluidos los pendientes.
  const requiresOTP = (firmaPendienteOtp && ["Finalizado", "Fallido"].includes(selectedEstado)) ||
    requiereOtpCierre(selectedEstado, isTAI, {
      tai_validacion_sms_activo: smsActivo, otp_traslados_normales_activo: otpNormalesActivo
    });

  // Determinar el tipo de OTP (validacion para FinalizadoXvalidar)
  const otpTipo = isPendienteValidacion ? "validacion" : (isTAI ? "tai" : "normal");

  // Calcular las opciones del mensaje basadas en el estado seleccionado
  const opcionesMensaje = useMemo(() => {
    const opciones = respuestasCierre[selectedEstado] || [];
    return opciones.map((respuesta) => ({
      label: respuesta,
      value: respuesta
    }));
  }, [selectedEstado]);

  // Inicializar formulario SOLO cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      const estadoInicial = filteredStates[0] || "Finalizado";
      form.setFieldsValue({
        estado: estadoInicial,
        mensaje: undefined
      });
      formPersistence.initializeForm({
        estado: estadoInicial,
        mensaje: "",
      });
    } else {
      // Limpiar cuando se cierra el diálogo
      formPersistence.clearPersistentState();
      form.resetFields();
      setOtpDialogOpen(false);
      setPendingSubmit(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Solo depender de 'open' para evitar reinicializaciones no deseadas

  // Manejar cambio de estado
  const handleEstadoChange = () => {
    // Solo limpiar el mensaje cuando cambia el estado
    form.setFieldsValue({
      mensaje: undefined
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (["Finalizado", "Fallido"].includes(values.estado) && (!configCargada || loadingSmsConfig)) return;
      if (values.mensaje && values.mensaje.trim()) {
        // Si requiere OTP, abrir el diálogo de verificación
        if (requiresOTP && trasladoId) {
          setPendingSubmit({
            estado: values.estado,
            mensaje: values.mensaje.trim()
          });
          setOtpDialogOpen(true);
        } else {
          // Cierre normal sin OTP
          onSubmit(values.estado, values.mensaje.trim());
          form.resetFields();
          onClose();
        }
      }
    } catch (error) {
      console.error("Error en la validación:", error);
    }
  };

  // Callback cuando OTP es verificado exitosamente
  const handleOTPVerified = (data) => {
    // El backend ya cerró el traslado, solo notificamos al padre
    if (pendingSubmit) {
      onSubmit(data?.status || pendingSubmit.estado, pendingSubmit.mensaje, true); // true indica que ya fue procesado
    }
    form.resetFields();
    setOtpDialogOpen(false);
    setPendingSubmit(null);
    onClose();
  };

  // Cerrar diálogo OTP sin completar
  const handleOTPClose = () => {
    setOtpDialogOpen(false);
    setPendingSubmit(null);
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Renderizar el Select con key única para forzar actualización
  const renderSelect = () => {
    return (
      <Select
        key={`select-${selectedEstado}-${opcionesMensaje.length}`}
        showSearch
        placeholder="Selecciona o busca el motivo del cierre"
        filterOption={(input, option) =>
          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        style={{ width: "100%" }}
        onFocus={() => formPersistence.markUserInput()}
        onChange={(value) => {
          form.setFieldsValue({ mensaje: value });
          formPersistence.saveFormValues();
        }}
        notFoundContent="No se encontraron opciones"
        allowClear
        options={opcionesMensaje}
      />
    );
  };

  return (
    <>
      <Modal
        open={open && !otpDialogOpen}
        title={
          <Text strong style={{ color: "#2E3B4E" }}>
            Cerrar Traslado
          </Text>
        }
        onCancel={handleCancel}
        footer={null}
        centered
        width={600}
        style={{ borderRadius: "15px", padding: "20px" }}
        styles={{ padding: "20px" }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            estado: "Finalizado",
            mensaje: "",
          }}
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Text
              type="secondary"
              style={{ color: "#7D879C", marginBottom: "20px" }}
            >
              Selecciona el estado con el que deseas cerrar el traslado:
            </Text>

            <Form.Item
              name="estado"
              rules={[
                {
                  required: true,
                  message: "Por favor selecciona un estado",
                },
              ]}
            >
              <Radio.Group onChange={handleEstadoChange}>
                <Space direction="vertical">
                  {filteredStates.map((estado) => (
                    <Radio key={estado} value={estado}>
                      {estado}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>

            {/* Alerta de verificación OTP */}
            {["Finalizado", "Fallido"].includes(selectedEstado) && (
              loadingSmsConfig ? (
                <div style={{ textAlign: "center", padding: 16 }}>
                  <Spin size="small" />
                  <Text type="secondary" style={{ marginLeft: 8 }}>
                    Verificando configuración...
                  </Text>
                </div>
              ) : !configCargada ? (
                <Alert type="error" showIcon message="No se pudo consultar la configuración OTP. Cierre y vuelva a abrir el formulario." />
              ) : requiresOTP ? (
                <Alert
                  type="info"
                  showIcon
                  icon={<SafetyOutlined />}
                  message="Verificación requerida"
                  description={
                    firmaPendienteOtp
                      ? `La firma del control de servicio fue retirada por novedad de firma ilegible del paciente. Para cerrar como ${selectedEstado} debe verificar el código OTP del firmante; la firma se actualizará en el mismo control de servicio.`
                      : isPendienteValidacion
                      ? `Para cerrar este traslado pendiente como ${selectedEstado}, debe verificar el código OTP del firmante.`
                      : isTAI
                        ? `Para cerrar este traslado TAI como ${selectedEstado}, se enviará un código de verificación por SMS al firmante.`
                        : `Para cerrar este traslado como ${selectedEstado}, se enviará un código de verificación por SMS al firmante.`
                  }
                  style={{ marginBottom: 16 }}
                />
              ) : !requiresOTP ? (
                <Alert
                  type="warning"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  message="Verificación SMS desactivada"
                  description={`La verificación OTP está desactivada para este tipo de servicio. Puede cerrar como ${selectedEstado} sin OTP.`}
                  style={{ marginBottom: 16 }}
                />
              ) : null
            )}

            <Form.Item
              name="mensaje"
              label={`Motivo del cierre (${opcionesMensaje.length} opciones disponibles para ${selectedEstado})`}
              rules={[
                {
                  required: true,
                  message: "Por favor selecciona el motivo del cierre",
                },
              ]}
              style={{ marginTop: "20px", width: "100%" }}
            >
              {renderSelect()}
            </Form.Item>

            <Space
              style={{
                justifyContent: "space-between",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <Button
                onClick={handleCancel}
                style={{
                  backgroundColor: "#F5F5F5",
                  color: "#7D879C",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  boxShadow: "none",
                }}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={["Finalizado", "Fallido"].includes(selectedEstado) && (!configCargada || loadingSmsConfig)}
                style={{
                  backgroundColor: requiresOTP ? "#722ed1" : "#1976D2",
                  color: "#fff",
                  padding: "10px 20px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                {requiresOTP ? "Continuar con Verificación" : "Cerrar Traslado"}
              </Button>
            </Space>
          </Space>
        </Form>
      </Modal>

      {/* Diálogo de verificación OTP */}
      {requiresOTP && (
        <OTPVerificationDialog
          open={otpDialogOpen}
          onClose={handleOTPClose}
          onVerified={handleOTPVerified}
          trasladoId={trasladoId}
          mensaje={pendingSubmit?.mensaje}
          tipo={otpTipo}
          estadoCierre={pendingSubmit?.estado || selectedEstado}
        />
      )}
    </>
  );
}

// Validación de las props con PropTypes
CerrarTrasladoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  allowedStates: PropTypes.array,
  isTAI: PropTypes.bool,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  estadoActualTraslado: PropTypes.string,
};

CerrarTrasladoDialog.defaultProps = {
  allowedStates: ["Finalizado", "Cancelado", "Fallido"],
  isTAI: false,
  trasladoId: null,
  estadoActualTraslado: null,
};

export default CerrarTrasladoDialog;
